'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
}

const FAQSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqs: FAQ[] = [
    {
      question: 'How long does a typical interior design project take?',
      answer: 'Timelines depend on scope and complexity. Typically, once the design is finalized, handover occurs within 45 days. We provide a detailed, project-specific timeline during consultation.'
    },

    {
      question: 'What is included in your design consultation?',
      answer: 'Our initial consultation includes site visit, requirement analysis, style preference discussion, preliminary budget estimation, and design approach overview. This 60-90 minute session helps us understand your vision and provides you with a clear project roadmap.'
    },
    {
      question: 'Do you work with NRI clients remotely?',
      answer: 'Yes! We have extensive experience managing projects for NRI clients. We offer video consultations, digital design presentations, regular photo/video updates, and dedicated project managers to ensure seamless communication throughout the project.'
    },
    {
      question: 'What is your payment structure?',
      answer: 'We follow a milestone-based payment structure: 5% advance, 45% on design approval, and 50% due 2 days prior to factory dispatch. We provide detailed invoices and maintain complete transparency.'
    },

    {
      question: 'Can I see your previous work before starting?',
      answer: 'Absolutely! We encourage visiting our completed projects. We can arrange site visits to similar projects. Our portfolio showcases diverse styles and budgets to help you visualize possibilities for your space.'
    },
    {
      question: 'Do you provide warranty on your work?',
      answer: 'Yes, we provide a 1-year comprehensive warranty on all workmanship and installations. Materials come with manufacturer warranties (10 years for modular products). We also offer annual maintenance contracts for long-term support.'
    },
    {
      question: 'How do you handle design changes during the project?',
      answer: 'Minor adjustments are accommodated within the approved design scope. Major changes are discussed with cost and timeline implications. We maintain detailed documentation and require written approval for significant modifications to ensure clarity.'
    },
    {
      question: 'What makes Hayat Interiors different from other designers?',
      answer: 'We specialize in blending modern minimalism with Indian cultural elements, creating spaces that are both contemporary and rooted in heritage. Our focus on sustainable local materials, transparent processes, and personalized service sets us apart.'
    }
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!isHydrated) {
    return (
      <div className="bg-white rounded-2xl border border-zinc-200 p-8 lg:p-12">
        <div className="h-8 bg-zinc-100 rounded w-1/2 mb-8 animate-pulse" />
        <div className="space-y-4">
          <div className="h-16 bg-zinc-100 rounded animate-pulse" />
          <div className="h-16 bg-zinc-100 rounded animate-pulse" />
          <div className="h-16 bg-zinc-100 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="bg-white rounded-2xl border border-zinc-200 p-6 md:p-8 lg:p-12">
      <div className="text-center mb-10 md:mb-12">
        <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-black/5 backdrop-blur-xl border border-black/10 mb-6">
          <div className="w-1.5 h-1.5 bg-black rounded-full" />
          <span className="text-xs font-medium text-black/90 tracking-widest uppercase">
            FAQ
          </span>
        </div>
        <h2 className="font-headline text-3xl md:text-4xl lg:text-5xl font-normal text-black tracking-tight leading-tight mb-4">
          Frequently Asked Questions
        </h2>
        <p className="text-black/70 font-normal max-w-2xl mx-auto">
          Find answers to common questions about our design process, timelines, and services
        </p>
      </div>

      <div className="space-y-4 max-w-4xl mx-auto">
        {faqs.map((faq, index) => (
          <div
            key={index}
            className="border border-zinc-200 rounded-2xl overflow-hidden transition-all duration-200 hover:border-[#1B5E4F]/50"
          >
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full flex items-center justify-between p-4 md:p-6 text-left bg-zinc-50 hover:bg-zinc-100 transition-colors min-h-[44px]"
            >
              <span className="font-medium text-black pr-4">
                {faq.question}
              </span>
              <Icon
                name="ChevronDownIcon"
                size={20}
                className={`text-[#1B5E4F] flex-shrink-0 transition-transform duration-200 ${
                  openIndex === index ? 'rotate-180' : ''
                }`}
              />
            </button>
            
            <div
              className={`overflow-hidden transition-all duration-300 ${
                openIndex === index ? 'max-h-96' : 'max-h-0'
              }`}
            >
              <div className="p-6 pt-0 bg-zinc-50">
                <p className="text-sm text-black/70 font-normal leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center">
        <p className="text-black/70 font-normal mb-4">
          Still have questions? We're here to help!
        </p>
        <button
          onClick={() => {
            const formSection = document.getElementById('contact-form');
            if (formSection) {
              formSection.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          className="inline-flex items-center space-x-2 px-6 py-3 bg-[#1B5E4F] text-white font-medium rounded-full hover:bg-[#1B5E4F]/90 transition-all duration-200 active:scale-95 min-h-[44px]"
        >
          <Icon name="ChatBubbleLeftRightIcon" size={20} />
          <span>Ask Your Question</span>
        </button>
      </div>
    </div>
    </>
  );
};

export default FAQSection;